import React from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Override react-toastify defaults to match the old vstui toast appearance:
 * white background, left-border accent, no type-colored indicators, no progress bar.
 */
const styleEl = document.createElement("style");
styleEl.textContent = `
  .Toastify__toast-container {
    width: auto;
    max-width: 320px;
  }
  .Toastify__toast {
    padding: 0;
    background-color: #ffffff;
    border-radius: 4px;
    box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);
    color: #333;
    cursor: default;
    min-height: auto;
  }
  .Toastify__toast-body {
    padding: 12px;
    margin: 0;
  }
  .Toastify__toast--default {
    background: #ffffff;
    color: #333;
  }
  .Toastify__close-button {
    color: #757575;
    opacity: 0.7;
    align-self: flex-start;
    padding: 6px;
  }
  .Toastify__close-button:hover {
    opacity: 1;
  }
  .Toastify__progress-bar {
    display: none;
  }
  /* Remove default type-colored left accent */
  .Toastify__toast--success,
  .Toastify__toast--error,
  .Toastify__toast--info,
  .Toastify__toast--warning {
    background: #ffffff;
    color: #333;
  }
`;
if (!document.querySelector("[data-toast-utils-styles]")) {
  styleEl.setAttribute("data-toast-utils-styles", "");
  document.head.appendChild(styleEl);
}

function ToastContent({ title, children, imageUrl, imgCoverStyle }) {
  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          style={{
            width: 60,
            height: "auto",
            marginRight: 10,
            flexShrink: 0,
            objectFit: imgCoverStyle === "multiple" ? "cover" : undefined
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
        )}
        {children}
      </div>
    </div>
  );
}

function mapPosition(horizontalPosition, verticalPosition) {
  const v = verticalPosition === "bottom" ? "bottom" : "top";
  const h = horizontalPosition === "left" ? "left" : "right";
  return `${v}-${h}`;
}

/**
 * Drop-in replacement for the removed VstToastUtils from vstui.
 * Wraps react-toastify's toast API directly.
 *
 * Supports two calling conventions:
 *   notify("message", { type: "error" })
 *   notify({ id, children, type, imageUrl, ... })
 */
export const toastUtils = {
  notify(messageOrProps, options = {}) {
    if (typeof messageOrProps === "string") {
      const toastType =
        options.type && options.type !== "none" ? options.type : "default";
      return toast(messageOrProps, {
        type: toastType,
        toastId: options.id,
        onClose: options.onDismiss,
        position: mapPosition(options.horizontalPosition),
        autoClose: options.autoClose ?? false
      });
    }

    const {
      id,
      children,
      onDismiss,
      type,
      title,
      imageUrl,
      imgCoverStyle,
      backgroundColor,
      borderColor,
      borderPosition,
      maxWidth,
      horizontalPosition,
      verticalPosition,
      dismissible,
      checkboxText,
      onCheckboxChange,
      imgBtnFn,
      ...rest
    } = messageOrProps;

    const content = (
      <ToastContent
        title={title}
        imageUrl={imageUrl}
        imgCoverStyle={imgCoverStyle}
      >
        {children}
        {checkboxText && (
          <label style={{ display: "block", marginTop: 8 }}>
            <input type="checkbox" onChange={onCheckboxChange} /> {checkboxText}
          </label>
        )}
      </ToastContent>
    );

    const toastType = type && type !== "none" ? type : "default";
    const position = mapPosition(horizontalPosition, verticalPosition);
    const border = borderColor
      ? `4px solid ${borderColor}`
      : undefined;
    const borderSide =
      borderPosition === "right" ? "borderRight" : "borderLeft";

    return toast(content, {
      toastId: id,
      type: toastType,
      onClose: onDismiss,
      position,
      closeButton: dismissible !== false,
      autoClose: rest.autoClose ?? false,
      style: {
        maxWidth: maxWidth || undefined,
        backgroundColor: backgroundColor || "#ffffff",
        [borderSide]: border
      }
    });
  },

  dismiss(id) {
    toast.dismiss(id);
  }
};
